'use client';
import useTranslation from '../../context/Translation/useTranslation';
import StarIcon from '@repo/ui/src/components/Icons/StarIcon';
import { classNames } from '@repo/ui/src/utils/classNames';

interface RatingProps {
  value: number | null | undefined;
  hideValue?: boolean;
  ratingText?: string;
  outOfText?: string;
  reviewCount?: number;
  className?: string;
  dark?: boolean;
  showCountInParentheses?: boolean;
  size?: 'xs' | 'small' | 'medium';
}

const RatingStars = ({
  value,
  hideValue,
  ratingText = 'Rating',
  outOfText = 'out of',
  reviewCount,
  className,
  dark = false,
  showCountInParentheses = false,
  size = 'medium',
}: RatingProps) => {
  const { t } = useTranslation();
  const _value = value ?? 0;
  const decimalPercent = Math.floor((_value % 1) * 100);
  const roundedValue = _value.toFixed(1);
  const floorValue = Math.floor(_value);
  const showReviewCount = reviewCount != null;
  const hasNoReviews = (showReviewCount && reviewCount === 0) || value == null;
  const _hideValue = hideValue || hasNoReviews;

  const getFillPercent = (index: number) => {
    if (hasNoReviews || index < floorValue) {
      return 100;
    }
    if (index === floorValue) {
      return decimalPercent;
    }
    return 0;
  };

  const reviewsLabel = t('genericContent.reviews', undefined, { default: 'Reviews' });
  const ariaLabel = hasNoReviews
    ? `${ratingText}: no reviews`
    : showReviewCount
      ? `${ratingText}: ${roundedValue} ${outOfText} 5, ${reviewCount} ${reviewsLabel}`
      : `${ratingText}: ${roundedValue} ${outOfText} 5`;

  return (
    <div
      className={classNames('flex gap-2 items-center align-middle', className)}
      role="region"
      aria-label={ariaLabel}
      data-testid="rating-stars"
    >
      <div
        className={classNames(
          'flex items-center',
          hasNoReviews ? 'text-[#949494]' : 'text-brand-400',
        )}
      >
        {[...Array(5)].map((x, i) => (
          <StarIcon
            key={i}
            fillPercent={getFillPercent(i)}
            className={classNames(
              size === 'xs' ? 'w-[14px]' : size === 'small' ? 'w-[18px]' : 'w-6',
              dark && 'text-brand-200',
            )}
          />
        ))}
      </div>
      {!_hideValue && (
        <span
          className={classNames('tra-text-web-body-l', dark && 'text-brand-200')}
          data-testid="rating-value"
        >
          {roundedValue}
        </span>
      )}
      {!_hideValue && showReviewCount && (
        <span className={classNames('self-stretch border-l-2 border-warm-400')} />
      )}
      {showReviewCount && (
        <div className="border-warm-400" data-testid="rating-count" aria-hidden="true">
          <p
            className={classNames(
              size === 'xs' ? 'tra-text-web-body-m' : 'tra-text-web-body-l',
              dark && 'text-brand-200',
            )}
          >
            {showCountInParentheses
              ? `(${reviewCount ?? 0})`
              : `${reviewCount ?? 0} ${reviewsLabel}`}
          </p>
        </div>
      )}
    </div>
  );
};

export default RatingStars;
